import { Link } from 'react-router-dom'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Bell } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { useAuthSession } from '@/features/auth/session'
import {
  fetchNotifications,
  markAllNotificationsRead,
  markNotificationRead,
} from '@/features/notifications/api'
import { NotificationDropdownItem } from '@/features/notifications/components/NotificationCard'
import { Button } from '@/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'
import type { NotificationListEnvelope } from '@/types/notifications'

export function NotificationBell() {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const { user } = useAuthSession()

  if (!user) {
    return null
  }

  const notificationsQuery = useQuery<NotificationListEnvelope>({
    queryKey: ['notifications', 'header'],
    queryFn: fetchNotifications,
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  })

  const markAllMutation = useMutation({
    mutationFn: markAllNotificationsRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const markOneMutation = useMutation({
    mutationFn: markNotificationRead,
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['notifications'] })
    },
  })

  const unreadCount = notificationsQuery.data?.meta.unread_count ?? 0
  const recentNotifications = (notificationsQuery.data?.data ?? []).slice(0, 4)

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          aria-label={t('notifications.title')}
        >
          <Bell className="h-4 w-4" />
          {unreadCount > 0 ? (
            <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-[#38BDF8] px-1 text-[10px] font-semibold text-white">
              {unreadCount > 9 ? '9+' : unreadCount}
            </span>
          ) : null}
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        align="end"
        sideOffset={6}
        className="w-80 overflow-hidden rounded-xl p-0"
      >
        <div className="flex items-center justify-between border-b px-3 py-2.5">
          <span className="font-semibold text-sm">
            {t('notifications.title')}
          </span>
          <Link
            to="/notifications"
            className="text-xs text-primary hover:underline"
          >
            {t('notifications.page.viewAll')}
          </Link>
        </div>

        <div className="max-h-[320px] overflow-y-auto">
          {recentNotifications.length === 0 ? (
            <div className="px-3 py-6 text-center text-sm text-muted-foreground">
              {t('notifications.page.noNewNotifications')}
            </div>
          ) : (
            recentNotifications.map((item) => (
              <NotificationDropdownItem
                key={item.id}
                item={item}
                onMarkRead={item.read_at ? undefined : () => markOneMutation.mutate(item.id)}
              />
            ))
          )}
        </div>

        {unreadCount > 0 && (
          <div className="border-t px-3 py-1.5">
            <Button
              type="button"
              variant="ghost"
              size="sm"
              className="w-full text-xs h-8"
              onClick={() => markAllMutation.mutate()}
            >
              {t('notifications.page.markAllRead')}
            </Button>
          </div>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
